import styled from '@emotion/styled'
import {
  EntityChangeList,
  EntityChangeListProps,
} from 'components/EntityChangeList'
import {
  ColumnRenderer,
  renderDate,
  renderPersona,
} from 'components/EntityList'
import { Box } from 'grid'
import { isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { Participant, ProvingUserRole, ResourceBooking, UserRole } from 'model'
import {
  ConstrainMode,
  Icon,
  mergeStyles,
  PersonaSize,
  SelectionMode,
  Stack,
  TooltipHost,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { createFieldColumn } from 'views/helpers'
import { useDoubleBookingsResourceBookingsListQuery } from 'views/ProvingDetailsView/hooks'

export type ResourceBookingListProps = EntityChangeListProps<
  Partial<ResourceBooking> & { _id: string }
>

const StyledBox = styled(Box)`
  padding-top: 3px;
`

export const ResourceBookingList: React.FunctionComponent<
  ResourceBookingListProps
> = ({ items, ...props }) => {
  const { t } = useTranslation()
  const auth = useAuthContext()
  const [proving] = useCurrentProving()
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )
  const isServiceTeam = isInProvingRole(proving, ProvingUserRole.ServiceTeam)
  const theme = useTheme()
  const iconClassRed = mergeStyles({
    color: theme.palette.red,
  })
  const renderResourceType = React.useMemo<ColumnRenderer>(
    () => (item, i, col) => {
      let category
      if (item.category === 'coldCell') {
        category = 'Kältezellen'
      } else if (item.category === 'building') {
        category = 'Gebäude'
      } else {
        category = ''
      }
      return t(`field.type.${item.type}`, { category })
    },
    [t]
  )
  const renderResourceCategory = React.useMemo<ColumnRenderer>(
    () => (item, i, col) => t(`field.category.${item}`),
    [t]
  )

  items = items.sort(
    (a, b) =>
      a.resource!.title.localeCompare(b.resource!.title, 'de', {
        numeric: true,
      })
    // a.resource!.title < b.resource!.title
    //   ? -1
    //   : a.resource!.title > b.resource!.title
    //   ? 1
    //   : 0
  )
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const { data, loading } = useDoubleBookingsResourceBookingsListQuery()
  const result =
    !loading && data && data.cachedProvings ? data.cachedProvings : []
  const checkForDoublebooking = React.useCallback(
    (rb: ResourceBooking) => {
      const doublebookings = [] as any[]
      const currentResourceBooking = rb || []
      result.forEach((prov) =>
        (prov.resourceBookings || []).forEach((resourceBooking: any) => {
          if (
            resourceBooking &&
            currentResourceBooking &&
            currentResourceBooking.resource &&
            (resourceBooking.resource ? resourceBooking.resource._id : '') ===
              currentResourceBooking.resource._id
          ) {
            if (
              resourceBooking.dateFrom <= rb.dateTo &&
              resourceBooking.dateTo >= rb.dateFrom
            ) {
              doublebookings.push({
                resourceBooking: currentResourceBooking,
              })
            }
          }
        })
      )
      let count = 0
      doublebookings.forEach((test) => {
        if (
          test.resourceBooking &&
          test.resourceBooking.resource &&
          currentResourceBooking.resource
        ) {
          if (
            test.resourceBooking.resource._id ===
            currentResourceBooking.resource._id
          ) {
            count++
            return count
          } else {
            return count
          }
        } else {
          return count
        }
      })
      return count
    },
    [result]
  )
  const columns = React.useMemo(
    () => [
      fieldColumn('type', {
        onRender: (item: ResourceBooking, i, col) => {
          return renderResourceType(item.resource)
        },
        getGroupValue: (item: ResourceBooking) =>
          t(`field.type.${item.resource.type}`),
        isSorted: true,

        ungroupable: true,
      }),
      fieldColumn('category', {
        onRender: (item: ResourceBooking, i, col) => {
          return renderResourceCategory(item.resource.category)
        },
        getGroupValue: (item: ResourceBooking) =>
          item.resource.category
            ? t(`field.category.${item.resource.category}`)
            : '',
        ungroupable: true,
        isGrouped: true,
      }),
      fieldColumn('parent.title', {
        onRender: (item: ResourceBooking, i, col) => {
          const value = item.resource.parent ? item.resource.parent!.title : ''
          return (
            <Box key={i} style={{ whiteSpace: 'pre-line' }}>
              {value}
            </Box>
          )
        },
        minWidth: 100,
        ungroupable: true,
      }),
      fieldColumn('resource', {
        onRender: (item: ResourceBooking, i, col) => {
          return (
            <Stack horizontal={true}>
              {checkForDoublebooking(item) > 1 && (
                <TooltipHost content={'Doppelbuchung'}>
                  <Stack.Item>
                    <StyledBox>
                      <Icon iconName="Important" className={iconClassRed} />
                    </StyledBox>
                  </Stack.Item>
                </TooltipHost>
              )}
              <Box key={i} style={{ whiteSpace: 'pre-line' }}>
                {item.resource.title}
              </Box>
              {item.resource.external && (
                <TooltipHost
                  content={'Temporäre Ressource'}
                  id={'temporaryResource'}
                >
                  <StyledBox>
                    <Icon
                      style={{ marginLeft: 4 }}
                      iconName={'Info'}
                      className={iconClassRed}
                      aria-describedby={'temporaryResource'}
                    />
                  </StyledBox>
                </TooltipHost>
              )}
            </Stack>
          )
        },
        getGroupValue: (item: ResourceBooking) =>
          item.resource.parent
            ? item.resource.parent!.title
            : item.resource.title,
        ungroupable: true,
        minWidth: 100,
      }),
      fieldColumn('dateFrom', { ungroupable: true, onRender: renderDate }),
      fieldColumn('dateTo', { ungroupable: true, onRender: renderDate }),
      fieldColumn('orderId', {
        minWidth: 100,
        ungroupable: true,
      }),
      fieldColumn('participantId', {
        onRender: (item: ResourceBooking, i, col) => {
          if (item.participantId) {
            const cParticipant = proving.participants!.find(
              (participant: Participant) =>
                participant._id === item.participantId
            )
            if (cParticipant) {
              // set different col fieldname for renderPersona
              col!.fieldName = 'person'
              return renderPersona({
                size: PersonaSize.size24,
              })(cParticipant, i, col)
            } else {
              return (
                <Box key={i} style={{ whiteSpace: 'pre-line' }}>
                  {t('field.messages.errorOruserDeleted')}
                </Box>
              )
            }
          }

          return (
            <Box key={i} style={{ whiteSpace: 'pre-line' }}>
              {''}
            </Box>
          )
        },
        minWidth: 155,
        ungroupable: true,
      }),
      fieldColumn('usageTime'),
    ],
    [checkForDoublebooking, fieldColumn, t]
  )

  return (
    <EntityChangeList
      alternate={true}
      stickyHeader={true}
      selectionMode={SelectionMode.multiple}
      items={items}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={columns}
      {...props}
    />
  )
}
